import { useState } from 'react'
import {
  AlertTriangle,
  ExternalLink,
  FileSearch,
  Phone,
  Search,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { AIPhoneNumber } from '@/api/aiAgents'

interface Props {
  isOpen: boolean
  onClose: () => void
  phoneNumbers: AIPhoneNumber[]
  companyMainNumber: string | null
  selectedNumberId: string
  onSelectNumber: (numberId: string) => void
  /** Permettre l'option "Aucun numéro" en mode édition. */
  allowNone?: boolean
  onNavigateToNumbers?: () => void
}

/** Port 1:1 du Next PhoneNumberSelectionModal — sélection numéro pour agent IA inbound. */
export function PhoneNumberSelectionModal({
  isOpen,
  onClose,
  phoneNumbers,
  companyMainNumber,
  selectedNumberId,
  onSelectNumber,
  allowNone = false,
  onNavigateToNumbers,
}: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  const handleSelect = (numberId: string) => {
    onSelectNumber(numberId)
    onClose()
  }

  const cleanSearch = (s: string) => s.replace(/[\s\-()+]/g, '')

  const filteredNumbers = phoneNumbers.filter((number) => {
    const cleanedNumber = cleanSearch(number.phone_number.toLowerCase())
    const cleanedQuery = cleanSearch(searchQuery.toLowerCase())
    return cleanedNumber.includes(cleanedQuery)
  })

  const companyMainMatches =
    companyMainNumber &&
    cleanSearch(companyMainNumber.toLowerCase()).includes(
      cleanSearch(searchQuery.toLowerCase()),
    )

  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith('+1')) {
      const digits = phone.substring(2)
      if (digits.length === 10) {
        return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
      }
    }
    return phone
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            {t('manager.aiAgents.assignPhoneNumber')}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t('manager.aiAgents.selectNumberForAgent')}
          </p>
        </DialogHeader>

        <div className="px-6 py-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('manager.aiAgents.searchPhoneNumber')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {/* Aucun numéro (mode édition) */}
          {allowNone && !searchQuery && (
            <button
              type="button"
              onClick={() => handleSelect('none')}
              className={cn(
                'w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b',
                selectedNumberId === 'none' && 'bg-blue-50 hover:bg-blue-50',
              )}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100">
                <X className="h-5 w-5 text-gray-500" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-sm">
                  {t('manager.aiAgents.noNumberOption')}
                </div>
                <div className="text-xs text-gray-500">
                  {t('manager.aiAgents.releaseAssignedNumber')}
                </div>
              </div>
              {selectedNumberId === 'none' && (
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              )}
            </button>
          )}

          {/* Numéro principal */}
          {companyMainNumber && (!searchQuery || companyMainMatches) && (
            <button
              type="button"
              onClick={() => handleSelect('main')}
              className={cn(
                'w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b',
                selectedNumberId === 'main' && 'bg-blue-50 hover:bg-blue-50',
              )}
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-100">
                <Phone className="h-5 w-5 text-yellow-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-sm flex items-center gap-2">
                  {formatPhoneNumber(companyMainNumber)}
                  <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                    <AlertTriangle className="h-3 w-3" />
                    {t('manager.aiAgents.main')}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {t('manager.aiAgents.companyMainNumber')}
                </div>
              </div>
              {selectedNumberId === 'main' && (
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              )}
            </button>
          )}

          {/* Liste des numéros */}
          {filteredNumbers.length === 0 &&
          (!companyMainNumber || (searchQuery && !companyMainMatches)) ? (
            <div className="px-6 py-12 text-center flex flex-col items-center">
              <div className="bg-blue-50 p-4 rounded-full mb-4">
                <FileSearch className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                {t('manager.aiAgents.noPhoneNumberFound')}
              </h3>
              <p className="text-sm text-gray-500 mb-6 max-w-[250px]">
                {t('manager.aiAgents.tryModifyingSearch')}
              </p>
              <Button
                variant="outline"
                className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => {
                  onClose()
                  onNavigateToNumbers?.()
                }}
              >
                {t('manager.aiAgents.addPhoneNumber')}
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            filteredNumbers.map((number) => (
              <button
                key={number.id}
                type="button"
                onClick={() => handleSelect(number.id.toString())}
                className={cn(
                  'w-full px-6 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b last:border-b-0',
                  selectedNumberId === number.id.toString() &&
                    'bg-blue-50 hover:bg-blue-50',
                )}
              >
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                  <Phone className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {formatPhoneNumber(number.phone_number)}
                    {number.assignment_type === 'main' && (
                      <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                        <AlertTriangle className="h-3 w-3" />
                        {t('manager.aiAgents.main')}
                      </span>
                    )}
                    {number.is_toll_free && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        {t('manager.aiAgents.tollFree')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {number.assignment_type === 'available'
                      ? t('manager.aiAgents.available')
                      : t('manager.aiAgents.assigned')}
                  </div>
                </div>
                {selectedNumberId === number.id.toString() && (
                  <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                )}
              </button>
            ))
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            {filteredNumbers.length + (companyMainNumber ? 1 : 0)}{' '}
            {t('manager.aiAgents.available').toLowerCase()}
          </p>
          <Button variant="ghost" onClick={onClose} size="sm">
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
